/**
 * Emissions KPI Cards
 *
 * Displays 4 summary cards: Total Emissions, Scope 1, Scope 2, Scope 3.
 * Uses MetricCard and FeaturedMetricCard from ui-modern.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Leaf } from 'lucide-react';
import { MetricCard, FeaturedMetricCard } from '@g17eco/ui-modern/components/ui/metric-card';
import { SCOPE_LUCIDE_ICONS, SCOPE_TAILWIND, SCOPE_COLORS } from '../config/scopeBranding';
import type { EmissionSummary } from '../types/emissions';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';

interface EmissionsKPICardsProps {
  summary?: EmissionSummary;
  previousSummary?: EmissionSummary;
  isLoading: boolean;
  previousLoading?: boolean;
  scopeUrls?: { scope1?: string; scope2?: string; scope3?: string };
}

const calculateChange = (current: number, previous: number): number | undefined => {
  if (previous === 0) return undefined;
  return ((current - previous) / previous) * 100;
};

export const EmissionsKPICards = ({
  summary,
  previousSummary,
  isLoading,
  previousLoading,
  scopeUrls,
}: EmissionsKPICardsProps) => {
  const { formatCompact } = useEmissionsFormatter();

  const totalEmissions = summary?.totalCO2e || 0;
  const scope1Emissions = summary?.byScope?.scope1 || 0;
  const scope2Emissions = summary?.byScope?.scope2 || 0;
  const scope3Emissions = summary?.byScope?.scope3 || 0;
  const hasScope3Data = summary !== undefined && summary.byScope?.scope3 !== undefined;

  const changes = useMemo(() => {
    if (!previousSummary) {
      return { total: undefined, scope1: undefined, scope2: undefined, scope3: undefined };
    }
    return {
      total: calculateChange(totalEmissions, previousSummary.totalCO2e || 0),
      scope1: calculateChange(scope1Emissions, previousSummary.byScope?.scope1 || 0),
      scope2: calculateChange(scope2Emissions, previousSummary.byScope?.scope2 || 0),
      scope3: calculateChange(scope3Emissions, previousSummary.byScope?.scope3 || 0),
    };
  }, [totalEmissions, scope1Emissions, scope2Emissions, scope3Emissions, previousSummary]);

  const showChangeSpinner = previousLoading && !previousSummary;

  const totalFormatted = formatCompact(totalEmissions);
  const scope1Formatted = formatCompact(scope1Emissions);
  const scope2Formatted = formatCompact(scope2Emissions);
  const scope3Formatted = formatCompact(scope3Emissions);

  const scopes = [
    {
      key: 'scope1',
      label: 'Scope 1',
      formatted: scope1Formatted,
      change: showChangeSpinner ? undefined : changes.scope1,
      href: scopeUrls?.scope1,
    },
    {
      key: 'scope2',
      label: 'Scope 2',
      formatted: scope2Formatted,
      change: showChangeSpinner ? undefined : changes.scope2,
      href: scopeUrls?.scope2,
    },
    {
      key: 'scope3',
      label: 'Scope 3',
      formatted: hasScope3Data ? scope3Formatted : { formatted: '—', unit: '' },
      change: hasScope3Data && scope3Emissions > 0 && !showChangeSpinner ? changes.scope3 : undefined,
      href: scopeUrls?.scope3,
    },
  ];

  return (
    <div className='tw:grid tw:grid-cols-2 tw:lg:grid-cols-4 tw:gap-4'>
      <FeaturedMetricCard
        label='Total Emissions'
        value={totalFormatted.formatted}
        unit={totalFormatted.unit}
        icon={<Leaf className='tw:size-5 tw:text-white' />}
        change={showChangeSpinner ? undefined : changes.total}
        isLoading={isLoading}
      />
      {scopes.map((scope) => {
        const tw = SCOPE_TAILWIND[scope.key];
        const Icon = SCOPE_LUCIDE_ICONS[scope.key];
        const card = (
          <MetricCard
            key={scope.key}
            label={scope.label}
            value={scope.formatted.formatted}
            unit={scope.formatted.unit}
            icon={<Icon className={`tw:size-5 ${tw.icon}`} />}
            iconBg={tw.iconBg}
            borderColor={tw.border}
            valueColor={SCOPE_COLORS[scope.key]}
            change={scope.change}
            isLoading={isLoading}
            className={scope.href ? 'tw:cursor-pointer tw:transition-shadow tw:hover:shadow-md' : undefined}
          />
        );
        if (!scope.href) {
          return card;
        }
        return (
          <Link key={scope.key} to={scope.href} className='tw:no-underline tw:text-inherit tw:block tw:h-full'>
            {card}
          </Link>
        );
      })}
    </div>
  );
};
